const preventScoreWriteQueues = new Map();
const preventScoreAuthorizations = new Map();

const getOwnerEncounterKey = (sessionFingerprint, encounterKey) => JSON.stringify([sessionFingerprint, encounterKey]);

const identityValue = (...values) => {
  const value = values.find(
    (candidate) =>
      (typeof candidate === "string" && candidate.trim() !== "") || (typeof candidate === "number" && Number.isFinite(candidate))
  );
  return value === undefined ? null : String(value);
};

export const getPreventScoreSessionFingerprint = (auth) => {
  const userIdentity = identityValue(auth?.user?.id, auth?.session?.userId, auth?.session?.id);
  const organizationIdentity = identityValue(
    auth?.session?.currentOrganizationId,
    auth?.session?.currentOrganization,
    auth?.session?.currentOrganizationName,
    auth?.orgInfo?.id
  );
  if (!userIdentity || !organizationIdentity) return null;
  return JSON.stringify([userIdentity, organizationIdentity]);
};

export const registerPreventScoreAuthorization = (sessionFingerprint, encounterKey, getIsEditable) => {
  if (!sessionFingerprint || !encounterKey || typeof getIsEditable !== "function") return () => {};
  const ownerEncounterKey = getOwnerEncounterKey(sessionFingerprint, encounterKey);
  let registrations = preventScoreAuthorizations.get(ownerEncounterKey);
  if (!registrations) {
    registrations = new Set();
    preventScoreAuthorizations.set(ownerEncounterKey, registrations);
  }
  const registration = { getIsEditable };
  registrations.add(registration);

  return () => {
    registrations.delete(registration);
    if (registrations.size === 0 && preventScoreAuthorizations.get(ownerEncounterKey) === registrations) {
      preventScoreAuthorizations.delete(ownerEncounterKey);
    }
  };
};

export const getPreventScoreAuthorization = (sessionFingerprint, encounterKey) => {
  const registrations = preventScoreAuthorizations.get(getOwnerEncounterKey(sessionFingerprint, encounterKey));
  if (!registrations) return null;
  let latestRegistration = null;
  registrations.forEach((registration) => {
    latestRegistration = registration;
  });
  return latestRegistration?.getIsEditable || null;
};

const notifyCancellation = (task) => {
  if (!task || task.cancellationNotified) return;
  task.cancellationNotified = true;
  task.onCancel();
};

const invalidateTask = (task) => {
  if (!task || !task.ownerValid) return false;
  task.ownerValid = false;
  task.cancelled = true;
  notifyCancellation(task);
  if (task.cancelRetry) task.cancelRetry();
  return true;
};

const cancelPendingWrite = (task) => {
  if (!task || task.started || task.cancelled) return false;
  return invalidateTask(task);
};

const waitForRetry = (task, queue, delay) => {
  if (!task.ownerValid || queue.cleared || (queue.pending && !queue.pending.cancelled)) {
    return Promise.resolve(false);
  }
  return new Promise((resolve) => {
    let settled = false;
    let timeoutId;
    const settle = (shouldRetry) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      if (task.cancelRetry === cancelRetry) task.cancelRetry = null;
      resolve(shouldRetry);
    };
    const cancelRetry = () => settle(false);
    timeoutId = setTimeout(() => settle(task.ownerValid && !queue.cleared), delay);
    task.cancelRetry = cancelRetry;
  });
};

const startPreventScoreWrite = (queueKey, queue, task) => {
  task.started = true;
  queue.activeTask = task;
  const controls = {
    isOwned: () => task.ownerValid && !task.cancelled && !queue.cleared,
    waitForRetry: (delay) => waitForRetry(task, queue, delay),
  };
  let writePromise;
  try {
    writePromise = Promise.resolve(task.run(controls));
  } catch (_error) {
    writePromise = Promise.resolve();
  }
  queue.active = writePromise;

  const continueQueue = () => {
    if (queue.active !== writePromise) return;
    queue.active = null;
    queue.activeTask = null;
    if (queue.cleared) return;
    const nextTask = queue.pending;
    queue.pending = null;
    if (nextTask && !nextTask.cancelled) {
      startPreventScoreWrite(queueKey, queue, nextTask);
    } else if (preventScoreWriteQueues.get(queueKey) === queue) {
      preventScoreWriteQueues.delete(queueKey);
    }
  };
  writePromise.then(continueQueue, continueQueue);
};

export const enqueuePreventScoreWrite = (sessionFingerprint, encounterKey, run, onCancel) => {
  if (!sessionFingerprint || !encounterKey) return () => {};
  const queueKey = getOwnerEncounterKey(sessionFingerprint, encounterKey);
  let queue = preventScoreWriteQueues.get(queueKey);
  if (!queue) {
    queue = { active: null, activeTask: null, pending: null, cleared: false };
    preventScoreWriteQueues.set(queueKey, queue);
  }

  const task = {
    run,
    onCancel,
    started: false,
    cancelled: false,
    ownerValid: true,
    cancellationNotified: false,
    cancelRetry: null,
  };
  if (queue.active) {
    cancelPendingWrite(queue.pending);
    queue.pending = task;
    if (queue.activeTask?.cancelRetry) queue.activeTask.cancelRetry();
  } else {
    startPreventScoreWrite(queueKey, queue, task);
  }

  return () => {
    if (!cancelPendingWrite(task)) return;
    if (queue.pending === task) queue.pending = null;
    if (!queue.active && !queue.pending && preventScoreWriteQueues.get(queueKey) === queue) {
      preventScoreWriteQueues.delete(queueKey);
    }
  };
};

export const clearAllPreventScoreWrites = () => {
  const queues = Array.from(preventScoreWriteQueues.values());
  preventScoreWriteQueues.clear();
  preventScoreAuthorizations.clear();
  queues.forEach((queue) => {
    queue.cleared = true;
    invalidateTask(queue.activeTask);
    invalidateTask(queue.pending);
    queue.pending = null;
  });
};
